import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { apiEndpoints } from 'src/app/api-endpoints';
import { Store, select } from '@ngrx/store';
import { BoardState } from '../board/+store/board-state/board-state.reducer';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { loadMetricListSuccess, updateMetricSuccess, clearMetrics, deleteMetricSuccess } from '../board/+store/board-state/board-state.actions';
import { selectMetricList } from '../board/+store/board-state/board-state.selectors';
import { MetricDto, MetricsDto } from './models/metric.models';
import { ApplicationIdRequest } from './models/application-details.model';

@Injectable({
  providedIn: 'root'
})
export class MetricService {
  private apiUrl = environment.apiConfig.baseUri + apiEndpoints.metricApis;
  private metricApis = {
    createMetric: '/create-metric',
    updateMetric: '/update-metric',
    deleteMetric: '/delete-metric',
    getMetricList: '/get-metrics-for-application',
  };

  constructor(
    private http: HttpClient,
    private boardStore: Store<BoardState>
  ) {}

  /**
   * Retrieves the metric list for the given application.
   *
   * - If the BoardState already contains datasets, returns them immediately.
   * - Otherwise, calls the backend API, dispatches an action to store the results, and returns the data.
   */
  getMetricList(): Observable<Map<number, MetricDto>> {
    return this.boardStore.pipe(
      select(selectMetricList),
      take(1), // Take the current state once
      switchMap(metricMap => {
        if (metricMap && metricMap.size > 0) {
          // metrics already exist in the store: return them as the metricMap.
          return of(metricMap);
        } else {
          // Otherwise, call the backend API to fetch the metrics.
          return this.http.post<MetricsDto>(
            `${this.apiUrl}${this.metricApis.getMetricList}`,
            {}
          ).pipe(
            filter(response => response !== null),
            map(metrics => {
              let metricMap = new Map<number, MetricDto>();
              metrics.metrics.forEach(element =>
                metricMap.set(
                  element.metricId,
                  element
                )
              );

              return metricMap;
            }),
            tap(metricMap => {
              // Dispatch an action to store the metrics in the state.
              this.boardStore.dispatch(loadMetricListSuccess({ metrics: metricMap }));
            })
          );
        }
      })
    );
  }

    /**
   * Updates the metric for the given dataset.
   *
   * - Updates BoardState upon success
   */
    updateMetric(metric: MetricDto): Observable<MetricDto> {
      return this.http.post<MetricDto>(
        `${this.apiUrl}${this.metricApis.updateMetric}`,
        metric
      ).pipe(
        tap((response: MetricDto) => {
          // Dispatch an action to store the metric in the state.
          if(response){
            this.boardStore.dispatch(updateMetricSuccess({ metric: response }));
          }
        }),
        catchError((error) => {
          // Handle the error here, such as logging it or displaying an error message
          console.error('Error updating metric:', error);
          // Return a safe fallback or rethrow the error as needed
          return throwError(() => new Error('Error updating metric'));
        })
      );
    }

        /**
   * Creates the new metric in the backend.
   *
   * - Updates BoardState upon success
   */
    createMetric(metric: MetricDto): Observable<MetricDto> {
      return this.http.post<MetricDto>(
        `${this.apiUrl}${this.metricApis.createMetric}`,
        metric
      ).pipe(
        tap((response: MetricDto) => {
          // Dispatch an action to store the datasets in the state.
          if(response){
            this.boardStore.dispatch(updateMetricSuccess({ metric: response }));
          }
        })
      );
    }

  /**
   * Deletes the metric from the backend.
   *
   * - Updates BoardState upon success
   */
  deleteMetric(metricId: number): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.apiUrl}${this.metricApis.deleteMetric}`,
      { metricId: metricId }
    ).pipe(
      tap((success: boolean) => {
        // Dispatch an action to remove the metric from the state.
        if(success){
          this.boardStore.dispatch(deleteMetricSuccess({ metricId: metricId }));
        }
      }),
      catchError((error) => {
        // Handle the error here, such as logging it or displaying an error message
        console.error('Error deleting metric:', error);
        // Return a safe fallback or rethrow the error as needed
        return throwError(() => new Error('Error deleting metric'));
      })
    );
  }

  /**
   * Clears the dataset references stored in the BoardState.
   */
  clearMetrics(): void {
    this.boardStore.dispatch(clearMetrics());
  }
}
